import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Trash2, X, ChevronLeft, Shield, Baby, Pencil } from "lucide-react";
import { AGE_GROUPS } from "@/lib/tracks";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";

// My Family portal — a signed-in user whose email matches a directory profile
// can see who's in their household. If they're a Guardian, they can add /
// remove / edit dependents themselves (e.g. a newborn) without waiting on the
// coordinator. Non-guardians see a read-only view of their family.

function AddDependentDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", gender: null, phone: "", email: "", age_group: "", is_guardian: false });
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null);

  const submit = async (force = false) => {
    if (!form.first_name.trim()) { toast.error("First name is required."); return; }
    setBusy(true);
    try {
      const r = await api.post("/members/my-family/new-person", form, { params: { force } });
      toast.success(`${formatDisplayName(r.data.person)} added to your family.`);
      onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && d.code === "duplicate_guest") {
        setDupes(d.duplicates || []);
      } else {
        toast.error(typeof d === "string" ? d : "Couldn't add.");
      }
    } finally { setBusy(false); }
  };

  if (dupes) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="myfam-add-dupes">
        <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
          <h3 className="font-serif-display text-xl mb-3" style={{ color: "var(--ink)" }}>Wait — we may already have them</h3>
          <p className="text-sm mb-3" style={{ color: "var(--ink-2)" }}>
            Someone with a similar name is already in the directory. Add anyway or go back and double-check?
          </p>
          <div className="space-y-2 mb-4 max-h-52 overflow-y-auto">
            {dupes.map((d) => (
              <div key={d.id} className="p-3 rounded-md text-sm" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)" }}>
                <div className="font-medium" style={{ color: "var(--ink)" }}>{formatDisplayName(d)}</div>
                <div className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{[d.phone, d.email, d.lifecycle_status].filter(Boolean).join(" · ")}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setDupes(null)} className="btn-ghost flex-1" data-testid="myfam-add-dupe-back">Back to form</button>
            <button onClick={() => { setDupes(null); submit(true); }} disabled={busy} className="btn-primary flex-1" data-testid="myfam-add-dupe-continue">Add anyway</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="myfam-add-dialog">
      <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Add someone to your family</h3>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="myfam-add-close"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="First name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="px-3 py-2 text-sm" data-testid="myfam-add-first" />
            <input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="px-3 py-2 text-sm" data-testid="myfam-add-last" />
          </div>
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="myfam-add-phone" />
          <input placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="myfam-add-email" />
          <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="myfam-add-age">
            <option value="">Age group…</option>
            {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="myfam-add-gender" />
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--ink-2)" }}>
            <input type="checkbox" checked={form.is_guardian} onChange={(e) => setForm({ ...form, is_guardian: e.target.checked })} className="h-4 w-4" style={{ accentColor: "var(--ember)" }} data-testid="myfam-add-guardian" />
            <span>Also a guardian (can check in the family)</span>
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button onClick={() => submit(false)} disabled={busy || !form.first_name.trim()} className="btn-primary flex-1" data-testid="myfam-add-submit">
            {busy ? "Adding…" : "Add to family"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditMemberDialog({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: member.first_name || "",
    last_name: member.last_name || "",
    gender: member.gender || null,
    phone: member.phone || "",
    age_group: member.age_group || "",
    is_guardian: !!member.is_guardian,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/members/my-family/members/${member.id}`, form);
      toast.success("Updated.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="myfam-edit-dialog">
      <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Edit {formatDisplayName(member, { firstOnly: true })}</h3>
          <button onClick={onClose} className="btn-ghost text-xs"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="px-3 py-2 text-sm" data-testid="myfam-edit-first" />
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="px-3 py-2 text-sm" data-testid="myfam-edit-last" />
          </div>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="w-full px-3 py-2 text-sm" data-testid="myfam-edit-phone" />
          <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="myfam-edit-age">
            <option value="">Age group…</option>
            {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="myfam-edit-gender" />
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--ink-2)" }}>
            <input type="checkbox" checked={form.is_guardian} onChange={(e) => setForm({ ...form, is_guardian: e.target.checked })} className="h-4 w-4" style={{ accentColor: "var(--ember)" }} data-testid="myfam-edit-guardian" />
            <span>Guardian (can check in the family)</span>
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary flex-1" data-testid="myfam-edit-submit">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyFamily() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/members/my-family");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load your family.");
      setData({ linked: false, message: "Sorry, something went wrong loading your family." });
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  const isGuardian = useMemo(() => !!(data?.me?.is_guardian), [data]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-sm" style={{ color: "var(--muted)" }}>Loading your family…</p>
      </div>
    );
  }

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${formatDisplayName(m, { firstOnly: true })} from your household? Their profile stays in the church directory.`)) return;
    try {
      await api.delete(`/members/my-family/members/${m.id}`);
      toast.success("Removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="my-family-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs mb-6" style={{ color: "var(--muted)" }}>
        <ChevronLeft size={12} /> Back to dashboard
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <Users size={24} style={{ color: "var(--ember)" }} />
        <h1 className="font-serif-display text-4xl" style={{ color: "var(--ink)" }}>My family</h1>
      </div>
      <p className="text-sm mb-8" style={{ color: "var(--ink-2)" }}>
        Add or edit family members so you can check them in for attendance and prayer with a single tap.
      </p>

      {!data.linked && (
        <div className="card-surface p-6 text-center" style={{ background: "var(--bg-alt)" }} data-testid="my-family-unlinked">
          <Baby size={28} style={{ color: "var(--muted)" }} className="mx-auto mb-3" />
          <p className="text-sm mb-1" style={{ color: "var(--ink-2)" }}>{data.message}</p>
          <p className="text-xs" style={{ color: "var(--muted)" }}>
            Once your coordinator adds you to the directory (using the email <em>{user.email}</em>), this page will light up.
          </p>
        </div>
      )}

      {data.linked && !data.household && (
        <div className="card-surface p-6 text-center" style={{ background: "var(--bg-alt)" }} data-testid="my-family-no-household">
          <Users size={28} style={{ color: "var(--muted)" }} className="mx-auto mb-3" />
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>{data.message}</p>
        </div>
      )}

      {data.linked && data.household && (
        <div className="card-surface p-6" style={{ background: "var(--bg-alt)" }} data-testid="my-family-card">
          <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
            <div>
              <div className="text-[0.65rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Your household</div>
              <div className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>{data.household.name}</div>
            </div>
            {isGuardian && (
              <button onClick={() => setAddOpen(true)} className="btn-primary text-sm" data-testid="my-family-add-btn">
                <Plus size={14} /> Add family member
              </button>
            )}
          </div>

          <div className="space-y-2">
            {/* Show self at the top */}
            <div className="rounded-md flex items-center justify-between px-4 py-3" style={{ background: "rgba(180, 128, 78, 0.10)", border: "1px solid var(--ember)", minHeight: "56px" }} data-testid="my-family-self">
              <div>
                <div className="text-lg font-medium" style={{ color: "var(--ink)" }}>
                  {formatDisplayName(data.me)} <span className="text-xs" style={{ color: "var(--muted)" }}>(you)</span>
                </div>
                {isGuardian && <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--ember)" }}><Shield size={11} /> Guardian</div>}
              </div>
            </div>
            {data.members.length === 0 ? (
              <p className="text-xs italic mt-4" style={{ color: "var(--muted)" }}>You're the only one in your household so far.</p>
            ) : (
              data.members.map((m) => (
                <div
                  key={m.id}
                  className="rounded-md flex items-center justify-between px-4 py-3"
                  style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)", minHeight: "56px" }}
                  data-testid={`my-family-member-${m.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-medium truncate" style={{ color: "var(--ink)" }}>{formatDisplayName(m)}</div>
                    <div className="text-xs flex items-center gap-2 mt-0.5" style={{ color: "var(--muted)" }}>
                      {m.age_group && <span>{m.age_group}</span>}
                      {m.is_guardian && <span className="inline-flex items-center gap-1" style={{ color: "var(--ember)" }}><Shield size={10} /> Guardian</span>}
                    </div>
                  </div>
                  {isGuardian && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(m)} className="btn-ghost text-xs" style={{ color: "var(--ink-2)" }} data-testid={`my-family-edit-${m.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => removeMember(m)} className="btn-ghost text-xs" style={{ color: "var(--terracotta)" }} data-testid={`my-family-remove-${m.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {!isGuardian && (
            <p className="text-xs mt-6 italic" style={{ color: "var(--muted)" }}>
              You're not marked as a guardian in this household. Ask your coordinator to promote you if you'd like to edit family members here.
            </p>
          )}
        </div>
      )}

      {addOpen && <AddDependentDialog onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />}
      {editing && <EditMemberDialog member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
